import { memo, useCallback, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { contractService } from "@/features/contracts/services/contract.service";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import type { NewContractData } from "../../utils/rental-create-payload";

interface ContractPickerFieldProps {
  selectedContractId: number | null;
  selectedContractLabel: string | null;
  newContract: NewContractData | null;
  onSelectExisting: (contract: ContractResponseDTO) => void;
  onCreateNew: () => void;
  onClear: () => void;
  error?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

export const ContractPickerField = memo(function ContractPickerField({
  selectedContractId,
  selectedContractLabel,
  newContract,
  onSelectExisting,
  onCreateNew,
  onClear,
  error,
}: ContractPickerFieldProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ContractResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchContracts = useCallback(
    async (rawTerm: string, signal: AbortSignal) => {
      const response = await contractService.search(
        { contractNumber: rawTerm.trim(), size: 10 },
        { signal },
      );
      setShowResults(true);
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term,
    enabled: !selectedContractId && !newContract,
    fetcher: fetchContracts,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar contratos")),
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  if (selectedContractId && selectedContractLabel) {
    return (
      <SelectedCard
        title="Contrato"
        primary={selectedContractLabel}
        onClear={onClear}
      />
    );
  }

  if (newContract) {
    return (
      <SelectedCard
        title="Contrato (novo)"
        primary={`Nº ${newContract.contractNumber}`}
        secondary={
          newContract.contractImage ? "Com imagem anexada" : "Sem imagem"
        }
        onClear={onClear}
      />
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="contract-search">Contrato *</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="contract-search"
            placeholder="Busque pelo número do contrato"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="pl-8"
          />
          {searching ? (
            <Spinner className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4" />
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onCreateNew}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Cadastrar novo
        </Button>
      </div>

      {showResults && results.length > 0 ? (
        <div className="rounded-md border border-input bg-background max-h-64 overflow-y-auto">
          {results.map((contract) => (
            <button
              type="button"
              key={contract.id}
              onClick={() => {
                onSelectExisting(contract);
                setTerm("");
                setShowResults(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border last:border-b-0"
            >
              <div className="text-sm font-medium">
                Nº {contract.contractNumber}
              </div>
              <div className="text-xs text-muted-foreground">
                Cliente: {contract.clientName}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {showResults && !searching && results.length === 0 && term.length >= 1 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum contrato encontrado.
        </p>
      ) : null}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
});

interface SelectedCardProps {
  title: string;
  primary: string;
  secondary?: string;
  onClear: () => void;
}

function SelectedCard({
  title,
  primary,
  secondary,
  onClear,
}: SelectedCardProps) {
  return (
    <div className="space-y-1">
      <Label>{title} *</Label>
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{primary}</div>
          {secondary ? (
            <div className="text-xs text-muted-foreground truncate">
              {secondary}
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          aria-label="Trocar contrato"
        >
          <X className="w-4 h-4" />
          <span className="ml-1">Trocar</span>
        </Button>
      </div>
    </div>
  );
}
